import { useState } from 'react'
import { api } from '../api/client'
import type { SessionInfo } from '../api/types'
import { Banner } from '../components/Banner'

interface Props {
  onSignedIn: (session: SessionInfo) => void
}

/**
 * The sign-in form.
 *
 * The server answers a wrong username and a wrong password identically, and this form
 * passes that message through unchanged rather than trying to be more helpful — being
 * more helpful here means confirming which usernames exist.
 */
export function Login({ onSignedIn }: Props) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      onSignedIn(await api.login(username.trim(), password))
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : 'Could not sign in.')
      setPassword('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login">
      <form className="card login__card" onSubmit={submit}>
        <h1 className="login__title">GA4 Briefing</h1>
        <p className="login__hint">Sign in to see the properties assigned to you.</p>

        {error && <Banner tone="critical">{error}</Banner>}

        <label className="field">
          <span className="field__label">Username</span>
          <input
            name="username"
            autoComplete="username"
            autoFocus
            required
            value={username}
            onChange={(event) => setUsername(event.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">Password</span>
          <input
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>

        <button className="button" type="submit" disabled={busy || !username || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  )
}
